/**
 * `DataTableHeaderRow` — the preset's entire header concern in one place: derive a cell per
 * visible column, wrap them in the sticky `TableHeader` band, and (when reordering is on)
 * in the drag-and-drop context. Split out of `data-table.tsx` so the orchestrator holds the
 * header as a single node rather than a cell-derivation block, a DnD wrapper and a JSX guard
 * spread ~250 lines apart.
 *
 * A HEADERLESS grid does not render this at all (the preset omits it), so nothing here has to
 * reason about `hideHeader` — the cells are never derived for a row that never mounts.
 */
import type { ReactNode } from 'react'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type SensorDescriptor,
  type SensorOptions,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import type {
  ColumnSizingState,
  RowData,
  SortingState,
  Table as TanStackTable,
} from '@tanstack/react-table'

import { resolveSortDirection } from './aria-sort'
import type { ColumnHeaderMenuLabels, ColumnHeaderMenuMove } from './column-header-menu'
import { readColumnMeta, readGrowColumnMinWidth } from './column-meta'
import { resolveColumnWidth } from './column-size'
import type { DataTableDensity, DataTableDivider, DataTableSurface } from './data-table.styles'
import { DataTableHeaderCell } from './data-table-header-cell'
import type { PinnedOffsetMap } from './pinning.styles'
import { SortableHeaderCell } from './sortable-header-cell'
import { TableHeader } from './table-parts'

/** Drag-to-reorder wiring. Present only while `enableColumnReordering` is on. */
export type DataTableHeaderReorder = {
  /** The reorderable (center, non-opted-out) column ids, in render order — the sortable items. */
  columnIds: string[]
  sensors: SensorDescriptor<SensorOptions>[]
  onDragEnd: (event: DragEndEvent) => void
}

export type DataTableHeaderRowProps<TData extends RowData> = {
  table: TanStackTable<TData>
  density: DataTableDensity
  /** The band's elevation. Also painted behind each cell's affordance chip, so it shows no seam. */
  surface: DataTableSurface
  /** The table's row-separator model — the band closes its bottom edge to match. */
  divider: DataTableDivider
  /** Leading selection checkbox cell (U8), built by the preset. Omit when there is no selection model. */
  selectionCell?: ReactNode
  /**
   * Reactive sort/size state, NOT read off the mutable `table` inside the cell: the React
   * Compiler memoizes the cells on the referentially-stable `table`, so a cell that read
   * `getIsSorted()` / `getSize()` would freeze its glyph, `aria-sort` and width while the body
   * re-sorts or re-sizes. Passing the state VALUES keys the cells to the live engine state.
   */
  sortingState: SortingState
  columnSizingState: ColumnSizingState
  /**
   * Whether a resize drag is live on any column. Threaded to every cell (not read off
   * `table` inside one) for the same compiler reason as the two state slices above, and
   * because it is a BAND-wide signal: the cell the pointer happens to be over is not the
   * cell being dragged. See `DataTableHeaderCell`'s prop doc for what it suppresses.
   */
  isAnyColumnResizing: boolean
  pinnedOffsets: PinnedOffsetMap
  /** Whether the body uses grow columns, which binds both planes to the scrollport. */
  fitScrollport: boolean
  /** The model-external selection column shifts every data column's `aria-colindex` by one. */
  hasSelectionColumn: boolean
  showColumnMenu: boolean
  columnMenuLabels?: Partial<ColumnHeaderMenuLabels>
  /** Keyboard "move left/right" controls; `undefined` for a non-reorderable column. */
  getColumnMove: (columnId: string) => ColumnHeaderMenuMove | undefined
  reorder?: DataTableHeaderReorder
}

export const DataTableHeaderRow = <TData extends RowData>({
  table,
  density,
  surface,
  divider,
  selectionCell,
  sortingState,
  columnSizingState,
  isAnyColumnResizing,
  pinnedOffsets,
  fitScrollport,
  hasSelectionColumn,
  showColumnMenu,
  columnMenuLabels,
  getColumnMove,
  reorder,
}: DataTableHeaderRowProps<TData>) => {
  const reorderableIdSet = new Set(reorder?.columnIds ?? [])
  const cells = table.getHeaderGroups().map((headerGroup) =>
    headerGroup.headers.map((header, headerIndex) => {
      const cellProps = {
        header,
        headerIndex,
        hasSelectionColumn,
        pinnedOffsets,
        showColumnMenu,
        columnMenuLabels,
        columnMove: getColumnMove(header.column.id),
        sortDirection: resolveSortDirection(sortingState, header.column.id),
        surface,
        width: resolveColumnWidth(header, columnSizingState),
        isAnyColumnResizing,
        grow: readColumnMeta(header.column)?.grow === true,
        growMinWidth: readGrowColumnMinWidth(header.column),
      }

      return reorderableIdSet.has(header.column.id) ? (
        <SortableHeaderCell
          key={header.id}
          {...cellProps}
        />
      ) : (
        <DataTableHeaderCell
          key={header.id}
          {...cellProps}
        />
      )
    }),
  )

  return (
    // Occupies `aria-rowindex` 1 (U4 convention) for a valid grid graph.
    <TableHeader
      density={density}
      divider={divider}
      fitScrollport={fitScrollport}
      rowIndex={1}
      surface={surface}
    >
      {selectionCell}
      {reorder === undefined ? (
        cells
      ) : (
        // DndContext/SortableContext render no DOM element, so the cells stay direct flex
        // children of TableHeader. `restrictToHorizontalAxis` keeps the drag on-row.
        <DndContext
          // A column drag is horizontal-only (`restrictToHorizontalAxis`), but dnd-kit's
          // auto-scroll still fires on the VERTICAL axis when the pointer crosses the body
          // rows below the header — scrolling the table mid-drag. Horizontal edge-scroll
          // isn't worth that: reorderable columns are also reachable via the header menu.
          autoScroll={false}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          sensors={reorder.sensors}
          onDragEnd={reorder.onDragEnd}
        >
          <SortableContext
            items={reorder.columnIds}
            strategy={horizontalListSortingStrategy}
          >
            {cells}
          </SortableContext>
        </DndContext>
      )}
    </TableHeader>
  )
}
